import { useCallback, useEffect, useRef, useState } from 'react';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import axios from 'axios';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { AtSign, CheckCheck, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  MobileBackButton,
  MobileHeader,
  MobileHeaderButton,
  SegmentedControl,
} from '@/components/mobile';
import { cn } from '@/lib/utils';
import { mentionApi, refreshMentions, mentionEvent } from './api';
export default function MentionsInbox({ team = false }: { team?: boolean }) {
  useDocumentTitle(team ? 'Team Mentions' : 'Mentions');
  const [unread, setUnread] = useState(true);
  const [items, setItems] = useState<any[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const load = useCallback(
    async (next?: string) => {
      const current = ++generation.current;
      setLoading(true);
      setError('');
      try {
        const { data } = await axios.get(mentionApi('', team), {
          withCredentials: true,
          params: { unread, cursor: next },
        });
        if (current !== generation.current) return;
        setItems((previous) =>
          next
            ? [
                ...previous,
                ...data.items.filter((item: any) => !previous.some((old) => old._id === item._id)),
              ]
            : data.items,
        );
        setCursor(data.nextCursor);
      } catch (error: any) {
        if (current === generation.current && [401, 403].includes(error.response?.status)) {
          setItems([]);
          setCursor(null);
        }
        if (current === generation.current)
          setError(error.response?.data?.message || 'Could not load mentions. Please try again.');
      } finally {
        if (current === generation.current) setLoading(false);
      }
    },
    [unread, team],
  );
  useEffect(() => {
    setItems([]);
    load();
    const refresh = () => {
      if (!document.hidden) load();
    };
    const interval = setInterval(refresh, 30000);
    window.addEventListener('focus', refresh);
    window.addEventListener(mentionEvent(team), refresh);
    return () => {
      generation.current++;
      clearInterval(interval);
      window.removeEventListener('focus', refresh);
      window.removeEventListener(mentionEvent(team), refresh);
    };
  }, [load, team]);
  const markAllRead = async () => {
    try {
      await axios.post(mentionApi('/read-all', team), {}, { withCredentials: true });
      await load();
      refreshMentions(team);
    } catch {
      setError('Could not mark mentions as read. Please try again.');
    }
  };
  const hasUnread = items.some((item) => !item.readAt);
  return (
    <main className="h-full overflow-y-auto bg-background max-md:overscroll-contain max-md:bg-muted/40 max-md:dark:bg-background">
      {/* Phones: a native navigation bar with the Unread | All switch. The
          filter is this component's state, so it survives a resize. */}
      <MobileHeader
        largeTitle
        title={team ? 'Team mentions' : 'Mentions'}
        left={
          <MobileBackButton
            fallback={team ? '/groupchat/groups' : '/inbox'}
            label={team ? 'Team' : undefined}
          />
        }
        right={
          <MobileHeaderButton
            aria-label="Mark all read"
            onClick={markAllRead}
            disabled={unread && !items.length}
          >
            <CheckCheck />
          </MobileHeaderButton>
        }
      >
        <div className="px-4 pb-3">
          <SegmentedControl
            aria-label="Mention status"
            value={unread ? 'unread' : 'all'}
            onValueChange={(value) => setUnread(value === 'unread')}
            options={[
              {
                value: 'unread',
                label: 'Unread',
                badge:
                  unread && hasUnread ? (
                    <span
                      aria-hidden="true"
                      className={cn(
                        'ml-1.5 inline-block size-1.5 rounded-full align-middle',
                        team ? 'bg-violet-600' : 'bg-blue-600',
                      )}
                    />
                  ) : undefined,
              },
              { value: 'all', label: 'All' },
            ]}
          />
        </div>
      </MobileHeader>
      <div className="mx-auto max-w-3xl p-4 sm:p-8 max-md:px-0 max-md:pb-[max(1.5rem,var(--safe-bottom))] max-md:pt-0">
        <div className="mb-6 flex items-center justify-between gap-3 max-md:hidden">
          <div>
            <h1 className="text-2xl font-semibold">{team ? 'Team mentions' : 'Mentions'}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {team
                ? 'Group and direct messages where teammates tagged you, across your locations.'
                : 'Notes where teammates tagged you, across your locations.'}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={markAllRead}>
            <CheckCheck className="mr-2 size-4" />
            Mark all read
          </Button>
        </div>
        <div className="mb-4 flex gap-2 max-md:hidden" role="tablist" aria-label="Mention status">
          {[true, false].map((value) => (
            <Button
              key={String(value)}
              role="tab"
              aria-selected={unread === value}
              variant={unread === value ? 'secondary' : 'ghost'}
              onClick={() => setUnread(value)}
            >
              {value ? 'Unread' : 'All'}
            </Button>
          ))}
        </div>
        {error && (
          <div
            role="alert"
            className="mb-4 rounded-lg border p-4 text-sm max-md:mx-4 max-md:mt-4 max-md:flex max-md:flex-wrap max-md:items-center max-md:justify-between max-md:gap-x-2 max-md:rounded-xl max-md:bg-background max-md:py-2 max-md:[&>button]:min-h-11"
          >
            {error}
            <Button variant="link" onClick={() => load()}>
              Retry
            </Button>
          </div>
        )}
        {!loading && !error && !items.length && (
          <div className="rounded-xl border py-16 text-center max-md:mx-4 max-md:mt-4 max-md:border-0 max-md:bg-transparent max-md:px-6">
            <AtSign className="mx-auto mb-3 size-8 text-muted-foreground" />
            <h2 className="font-medium">{unread ? 'You’re all caught up' : 'No mentions yet'}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {team
                ? 'When someone tags you in a group chat or direct message, it will appear here.'
                : 'When someone tags you in a customer note, it will appear here.'}
            </p>
          </div>
        )}
        <div className="divide-y rounded-xl border empty:border-0 max-md:mx-4 max-md:mt-4 max-md:overflow-hidden max-md:border-0 max-md:bg-background max-md:ring-1 max-md:ring-border/70 max-md:empty:hidden dark:max-md:bg-zinc-900 dark:max-md:ring-white/10">
          {items.map((item) => (
            <Link
              key={item._id}
              to={`/${team ? 'team-mentions' : 'mentions'}/${item._id}`}
              className="flex gap-3 p-4 hover:bg-muted/50 focus-visible:outline focus-visible:outline-2 max-md:min-h-[72px] max-md:gap-2.5 max-md:py-3 max-md:pl-3 max-md:pr-4 max-md:transition-colors max-md:active:bg-muted/60"
            >
              <span
                className={`mt-2 size-2 shrink-0 rounded-full ${item.readAt ? 'bg-transparent' : team ? 'bg-violet-600' : 'bg-blue-600'} max-md:size-2.5`}
              >
                {!item.readAt && <span className="sr-only">Unread. </span>}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm max-md:text-[15px] max-md:leading-snug">
                  <strong>{item.actorName}</strong>{' '}
                  {team ? 'tagged you in' : 'tagged you in a note for'}{' '}
                  <strong>{team ? item.groupTitle : item.customerName}</strong>
                </p>
                <p className="mt-1 line-clamp-2 text-sm text-muted-foreground max-md:mt-0.5 max-md:text-[15px] max-md:leading-snug">
                  {item.unavailable
                    ? team
                      ? 'This message was deleted.'
                      : 'This note was deleted.'
                    : item.excerpt}
                </p>
                <p className="mt-2 text-xs text-muted-foreground max-md:mt-1 max-md:text-[13px]">
                  {item.businessName} · {moment(item.createdAt).format('lll')}
                </p>
              </div>
            </Link>
          ))}
        </div>
        {loading && (
          <Loader2 aria-label="Loading mentions" className="mx-auto my-6 size-5 animate-spin" />
        )}
        {cursor && !loading && (
          <Button
            variant="outline"
            className="mt-4 max-md:mx-4 max-md:h-11 max-md:w-[calc(100%-2rem)]"
            onClick={() => load(cursor)}
          >
            Load more
          </Button>
        )}
      </div>
    </main>
  );
}
